import { useEffect, useRef, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSocket } from '@/contexts/SocketContext';
import { trackerAPI } from '../api/tracker.api';

// Query keys for React Query deduplication
const UNREAD_COUNT_KEY = ['tracker', 'unread', 'count'];
const UNREAD_AFFAIRES_KEY = ['tracker', 'unread', 'affaires'];

/**
 * Hook to fetch and manage unread count for tracker channels
 * Uses React Query for automatic request deduplication across multiple hook instances
 * Listens to WebSocket events for real-time updates with debouncing
 * Falls back to polling if WebSocket is disconnected
 */
export const useTrackerUnreadCount = () => {
  const { socket, isConnected } = useSocket();
  const queryClient = useQueryClient();
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch unread count using React Query (deduplicated across instances)
  const {
    data: countData,
    isLoading: isCountLoading,
    refetch: refetchCount
  } = useQuery({
    queryKey: UNREAD_COUNT_KEY,
    queryFn: async () => {
      const response = await trackerAPI.getUnreadCount();
      return response.success ? response.data.unread_count : 0;
    },
    staleTime: 30 * 1000, // Consider data fresh for 30 seconds
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    refetchOnMount: true, // Always fetch on mount
    refetchOnWindowFocus: false, // Don't refetch on window focus (handled by WebSocket)
  });

  // Fetch unread affaire IDs using React Query (deduplicated across instances)
  const {
    data: affairesData,
    isLoading: isAffairesLoading,
    refetch: refetchAffaires
  } = useQuery({
    queryKey: UNREAD_AFFAIRES_KEY,
    queryFn: async () => {
      const response = await trackerAPI.getUnreadAffaires();
      return response.success ? response.data.affaire_ids : [];
    },
    staleTime: 30 * 1000, // Consider data fresh for 30 seconds
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    refetchOnMount: true, // Always fetch on mount
    refetchOnWindowFocus: false, // Don't refetch on window focus (handled by WebSocket)
  });

  // Convert affaire IDs array to Set for efficient lookup
  const unreadAffaireIds = useMemo(() => {
    return new Set(affairesData || []);
  }, [affairesData]);

  // Debounced refetch function to prevent rapid-fire API calls
  const debouncedRefetch = useCallback(() => {
    // Clear any pending debounce
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Set a new debounce timeout (300ms)
    debounceTimeoutRef.current = setTimeout(() => {
      // Invalidate queries to trigger refetch
      queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_KEY });
      queryClient.invalidateQueries({ queryKey: UNREAD_AFFAIRES_KEY });
    }, 300);
  }, [queryClient]);

  // Combined refetch for external use - invalidates cache to force fresh fetch
  const refetch = useCallback(async () => {
    // Invalidate the cache to force a fresh fetch (bypasses staleTime)
    await queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_KEY });
    await queryClient.invalidateQueries({ queryKey: UNREAD_AFFAIRES_KEY });
  }, [queryClient]);

  // WebSocket listeners for real-time updates
  useEffect(() => {
    if (!socket || !isConnected) return;

    // Create specific handler references for proper cleanup
    // (must not remove handlers from other hooks like useTrackerNotifications)
    const handleTaskCreated = () => debouncedRefetch();
    const handleTaskUpdated = () => debouncedRefetch();
    const handleCommentAdded = () => debouncedRefetch();
    const handleActivityUpdated = () => debouncedRefetch();
    const handleCommunicationAssigned = () => debouncedRefetch();
    const handleCommunicationCompleted = () => debouncedRefetch();
    const handleComCreated = () => debouncedRefetch();
    const handleComUpdated = () => debouncedRefetch();
    const handleNotificationsCleared = () => debouncedRefetch();
    const handleStatusChanged = () => debouncedRefetch();

    // Listen for tracker-specific events
    socket.on('tracker:task:created', handleTaskCreated);
    socket.on('tracker:task:updated', handleTaskUpdated);
    socket.on('tracker:task:comment:added', handleCommentAdded);
    socket.on('tracker:activity:updated', handleActivityUpdated);
    // Communication-related events (for tagging/assigning)
    socket.on('tracker:communication:assigned', handleCommunicationAssigned);
    socket.on('tracker:communication:completed', handleCommunicationCompleted);
    socket.on('tracker:com:created', handleComCreated);
    socket.on('tracker:com:updated', handleComUpdated);
    // Notification cleared event (syncs all instances)
    socket.on('tracker:notifications:cleared', handleNotificationsCleared);
    // Status change event (for status update notifications)
    socket.on('tracker:status:changed', handleStatusChanged);

    // Cleanup - remove only OUR handlers, not handlers from other hooks
    return () => {
      socket.off('tracker:task:created', handleTaskCreated);
      socket.off('tracker:task:updated', handleTaskUpdated);
      socket.off('tracker:task:comment:added', handleCommentAdded);
      socket.off('tracker:activity:updated', handleActivityUpdated);
      socket.off('tracker:communication:assigned', handleCommunicationAssigned);
      socket.off('tracker:communication:completed', handleCommunicationCompleted);
      socket.off('tracker:com:created', handleComCreated);
      socket.off('tracker:com:updated', handleComUpdated);
      socket.off('tracker:notifications:cleared', handleNotificationsCleared);
      socket.off('tracker:status:changed', handleStatusChanged);

      // Clear any pending debounce on cleanup
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [socket, isConnected, debouncedRefetch]);

  // Fallback polling when WebSocket is disconnected
  useEffect(() => {
    if (!isConnected) {
      // Poll every 30 seconds when WebSocket is disconnected
      pollingIntervalRef.current = setInterval(() => {
        queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_KEY });
        queryClient.invalidateQueries({ queryKey: UNREAD_AFFAIRES_KEY });
      }, 30000);
    } else {
      // Clear polling when WebSocket is connected
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }

    // Cleanup on unmount
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [isConnected, queryClient]);

  return {
    unreadCount: countData ?? 0,
    unreadAffaireIds,
    isLoading: isCountLoading || isAffairesLoading,
    refetch
  };
};

export default useTrackerUnreadCount;
